import React from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/ui/components/primitives/button";
import { Card } from "@/ui/components/primitives/card";

interface AsyncErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface AsyncErrorState {
  hasError: boolean;
  error?: Error;
}

export function AsyncErrorBoundary({ children, fallback }: AsyncErrorBoundaryProps) {
  const [state, setState] = React.useState<AsyncErrorState>({ hasError: false });

  const throwAsyncError = React.useCallback((error: Error) => {
    setState({ hasError: true, error });
  }, []);

  // Handle unhandled promise rejections
  React.useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      throwAsyncError(event.reason instanceof Error ? event.reason : new Error(String(event.reason)));
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  }, [throwAsyncError]);

  const handleRetry = () => {
    setState({ hasError: false, error: undefined });
  };

  if (state.hasError) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <Card className="p-6 text-center">
        <div className="flex justify-center mb-4">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>

        <h3 className="text-lg font-semibold text-foreground mb-2">
          PayGate Connection Error
        </h3>

        <p className="text-muted-foreground mb-4">
          {state.error?.message || 'Failed to load PayGate data. Please check your connection.'}
        </p>

        <div className="space-y-2">
          <Button onClick={handleRetry} className="w-full">
            Try Again
          </Button>
          <Button variant="outline" onClick={() => window.location.reload()} className="w-full">
            Reload Page
          </Button>
        </div>

        {process.env.NODE_ENV === 'development' && state.error && (
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-sm text-muted-foreground">
              Error Details (Development)
            </summary>
            <pre className="text-xs bg-muted p-3 rounded mt-2 overflow-auto">
              {state.error.stack}
            </pre>
          </details>
        )}
      </Card>
    );
  }

  return <>{children}</>;
}

// Hook for throwing async errors in React
export function useAsyncError() {
  const [state, setState] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (state) {
      throw state;
    }
  }, [state]);

  return React.useCallback((error: Error) => {
    setState(error instanceof Error ? error : new Error(String(error)));
  }, []);
}